import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import FinDataGrid from "../components/findatagrid";

const API_BASE = process.env.REACT_APP_API_URI;
const TOKEN_PARAMS = ["max_tokens", "max_completion_tokens"];
// Diese Provider haben feste Regeln im Adapter, Stream und Reasoning wirken dort nicht.
const FIXED_ADAPTERS = new Set(["openai", "mistral", "deepseek"]);

const KEY_STATUS_TEXT = {
  env: "aus der Umgebung",
  env_fallback: "Env-Fallback, gespeicherter Key nicht lesbar",
  missing: "fehlt",
  not_required: "nicht nötig",
  undecryptable: "nicht entschlüsselbar",
};

const EMPTY_FORM = {
  name: "",
  label: "",
  base_url: "",
  api_key: "",
  clear_api_key: false,
  api_key_env: "",
  base_url_env: "",
  is_local: false,
  token_param: "max_tokens",
  sends_reasoning_off: false,
  stream: false,
  timeout_ms: "",
  max_concurrency: "",
  extra_body: "",
};

export async function readErrorMessage(response) {
  const body = await response.json().catch(() => null);
  return body?.message || `Fehler ${response.status}`;
}

export function formatTestResult(row) {
  if (!row.last_test_at) return "noch nicht getestet";
  const latency = row.last_test_latency_ms != null ? ` (${row.last_test_latency_ms} ms)` : "";
  if (row.last_test_ok === true) return `OK${latency}`;
  if (row.last_test_ok === false) return `Fehler${latency}`;
  return `unklar${latency}`;
}

export function describeTestResult(row) {
  if (!row.last_test_at) return "";
  return `${new Date(row.last_test_at).toLocaleString("de-DE")}: ${row.last_test_message || ""}`;
}

const isActive = (row) => row.status === "active";

function toForm(row) {
  return {
    ...EMPTY_FORM,
    ...row,
    label: row.label || "",
    base_url: row.base_url || "",
    api_key: "",
    clear_api_key: false,
    api_key_env: row.api_key_env || "",
    base_url_env: row.base_url_env || "",
    timeout_ms: row.timeout_ms ?? "",
    max_concurrency: row.max_concurrency ?? "",
    extra_body: row.extra_body ? JSON.stringify(row.extra_body) : "",
  };
}

// Leer gelassenes Key-Feld heißt unverändert: Es geht gar nicht erst mit.
function toPayload(form) {
  const {
    id, status, archived_at, api_key_hint, has_stored_key, key_status, key_message, effective_url, url_from_env,
    last_test_at, last_test_ok, last_test_latency_ms, last_test_message, model_count, active_models, jobs,
    api_key, clear_api_key, ...fields
  } = form;
  const payload = { ...fields };
  if (api_key.trim()) payload.api_key = api_key.trim();
  if (clear_api_key) payload.clear_api_key = true;
  return payload;
}

const AdminAiProviders = ({ providers, onChanged }) => {
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [testModel, setTestModel] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const visible = showArchived ? providers : providers.filter(isActive);
  const isNew = !form.id;
  const update = (fields) => setForm((current) => ({ ...current, ...fields }));

  const openDialog = (row) => {
    setForm(row ? toForm(row) : EMPTY_FORM);
    setDialogError(null);
    setTestResult(null);
    setTestModel("");
    setDialogOpen(true);
  };

  // Der Key bleibt nicht im State, sobald der Dialog zu ist.
  const closeDialog = () => {
    setDialogOpen(false);
    setForm(EMPTY_FORM);
    setTestResult(null);
  };

  const handleSave = async () => {
    setDialogError(null);
    try {
      const response = await fetch(`${API_BASE}/api/ai_providers${isNew ? "" : `/${form.id}`}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toPayload(form)),
      });
      if (!response.ok) {
        setDialogError(await readErrorMessage(response));
        return;
      }
      const saved = await response.json();
      setNotice(saved.key_discarded
        ? `Der Host von "${saved.name}" hat sich geändert. Der hinterlegte Key wurde verworfen, bitte neu eintragen.`
        : null);
      closeDialog();
      onChanged();
    } catch (err) {
      setDialogError(err.message);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setDialogError(null);
    setTestResult(null);
    try {
      const body = { ...toPayload(form) };
      if (testModel.trim()) body.model = testModel.trim();
      const response = await fetch(`${API_BASE}/api/ai_providers/${isNew ? "new" : form.id}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        setDialogError(await readErrorMessage(response));
        return;
      }
      setTestResult(await response.json());
      onChanged();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setTesting(false);
    }
  };

  const runAction = async (row, path, method) => {
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/ai_providers/${row.id}${path}`, { method });
      if (!response.ok) setError(await readErrorMessage(response));
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleArchive = (row) => {
    if (!window.confirm(`Provider "${row.name}" archivieren? Er verschwindet aus den Auswahllisten.`)) return;
    runAction(row, "/archive", "POST");
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Provider "${row.name}" wirklich löschen?`)) return;
    runAction(row, "", "DELETE");
  };

  const columns = [
    { field: "name", headerName: "Provider", width: 130, cellClassName: "name-column--cell" },
    { field: "label", headerName: "Bezeichnung", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 90,
      valueGetter: (params) => (isActive(params.row) ? "aktiv" : "archiviert"),
    },
    {
      field: "effective_url",
      headerName: "URL",
      width: 240,
      renderCell: (params) => (
        <Tooltip title={params.row.url_from_env ? `aus ${params.row.base_url_env}` : ""}>
          <span>{params.value || "nicht gesetzt"}</span>
        </Tooltip>
      ),
    },
    {
      field: "key",
      headerName: "Key",
      width: 190,
      valueGetter: (params) => (params.row.key_status === "stored"
        ? params.row.api_key_hint
        : KEY_STATUS_TEXT[params.row.key_status]),
      renderCell: (params) => (
        <Tooltip title={params.row.key_message || params.row.api_key_env || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "last_test",
      headerName: "Letzter Test",
      width: 170,
      valueGetter: (params) => formatTestResult(params.row),
      renderCell: (params) => (
        <Tooltip title={describeTestResult(params.row)}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    { field: "model_count", headerName: "Modelle", width: 80, type: "number" },
    {
      field: "jobs",
      headerName: "Jobs",
      width: 180,
      valueGetter: (params) => (params.row.jobs || []).map((job) => job.name).join(", "),
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "editBtn",
      headerName: "Edit",
      width: 80,
      renderCell: (params) => (
        <Button color="inherit" size="small" onClick={() => openDialog(params.row)}>Edit</Button>
      ),
    },
    {
      field: "archiveBtn",
      headerName: "Archiv",
      width: 130,
      renderCell: (params) => {
        const { row } = params;
        if (!isActive(row)) {
          return <Button color="inherit" size="small" onClick={() => runAction(row, "/restore", "POST")}>Wiederherstellen</Button>;
        }
        const inUse = (row.active_models || []).length > 0 || (row.jobs || []).length > 0;
        return (
          <Tooltip title={inUse ? "Wird noch von Modellen oder Jobs genutzt" : ""}>
            <span>
              <Button color="inherit" size="small" disabled={inUse} onClick={() => handleArchive(row)}>Archivieren</Button>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "deleteBtn",
      headerName: "Delete",
      width: 80,
      renderCell: (params) => {
        if (params.row.is_local || params.row.model_count > 0 || (params.row.jobs || []).length > 0) return null;
        return <Button color="error" size="small" onClick={() => handleDelete(params.row)}>Del</Button>;
      },
    },
  ];

  const keyHelp = form.has_stored_key
    ? `Hinterlegt: ${form.api_key_hint}. Leer lassen = unverändert.`
    : "Wird verschlüsselt gespeichert. Leer lassen = unverändert.";

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5">AI Provider</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
            label="Archivierte anzeigen"
          />
          <Button variant="outlined" color="secondary" onClick={() => openDialog(null)}>Provider hinzufügen</Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      <FinDataGrid id="gridProviders" height="30vh" density="compact" rows={visible} columns={columns} />

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isNew ? "Provider hinzufügen" : `Provider ${form.name} bearbeiten`}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 1 }}>{dialogError}</Alert>}
          <TextField
            autoFocus={isNew}
            margin="dense"
            label="Name (Schlüssel)"
            fullWidth
            variant="standard"
            value={form.name}
            disabled={!isNew}
            helperText="Kleinbuchstaben, Ziffern, - und _. Nicht änderbar."
            onChange={(e) => update({ name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Bezeichnung"
            fullWidth
            variant="standard"
            value={form.label}
            onChange={(e) => update({ label: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Basis-URL"
            fullWidth
            variant="standard"
            value={form.base_url}
            placeholder="https://api.example.com/v1"
            helperText={form.url_from_env
              ? `Wirksam ist ${form.effective_url} aus ${form.base_url_env}.`
              : "Inklusive /v1. Ändert sich der Host, wird der hinterlegte Key verworfen."}
            onChange={(e) => update({ base_url: e.target.value })}
          />
          <TextField
            margin="dense"
            label="API-Key"
            type="password"
            autoComplete="new-password"
            fullWidth
            variant="standard"
            value={form.api_key}
            helperText={keyHelp}
            onChange={(e) => update({ api_key: e.target.value })}
          />
          {form.has_stored_key && (
            <FormControlLabel
              control={<Checkbox checked={form.clear_api_key} onChange={(e) => update({ clear_api_key: e.target.checked })} />}
              label="Hinterlegten Key löschen"
            />
          )}
          <TextField
            margin="dense"
            label="Env-Variable für den Key (Fallback)"
            fullWidth
            variant="standard"
            value={form.api_key_env}
            helperText="AI_KEY_<NAME> oder eine der festen Variablen. Feste Env-Keys gelten nur für ihren eigenen Host."
            onChange={(e) => update({ api_key_env: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Env-Variable für die URL"
            fullWidth
            variant="standard"
            value={form.base_url_env}
            helperText="LLM_STUDIO_URL oder AI_URL_<NAME>. Gesetzt gewinnt sie über die Basis-URL."
            onChange={(e) => update({ base_url_env: e.target.value })}
          />
          <FormControlLabel
            control={<Checkbox checked={Boolean(form.is_local)} onChange={(e) => update({ is_local: e.target.checked })} />}
            label="Lokal (Slots je Backend, Thinking-Regeln wie llmproxy)"
          />
          <FormControl fullWidth variant="standard" margin="dense">
            <InputLabel>Token-Parameter</InputLabel>
            <Select value={form.token_param} onChange={(e) => update({ token_param: e.target.value })}>
              {TOKEN_PARAMS.map((param) => <MenuItem key={param} value={param}>{param}</MenuItem>)}
            </Select>
          </FormControl>
          {FIXED_ADAPTERS.has(form.name) ? (
            <Alert severity="info" sx={{ my: 1 }}>
              {form.name} hat feste Regeln im Adapter. Stream und Reasoning lassen sich hier nicht umstellen.
            </Alert>
          ) : (
            <Box>
              <FormControlLabel
                control={<Checkbox checked={Boolean(form.sends_reasoning_off)} onChange={(e) => update({ sends_reasoning_off: e.target.checked })} />}
                label="Reasoning abschalten (reasoning_effort: none)"
              />
              <FormControlLabel
                control={<Checkbox checked={Boolean(form.stream)} onChange={(e) => update({ stream: e.target.checked })} />}
                label="Streaming"
              />
            </Box>
          )}
          <Box display="flex" gap={2}>
            <TextField
              margin="dense"
              label="Timeout in ms"
              fullWidth
              variant="standard"
              value={form.timeout_ms}
              onChange={(e) => update({ timeout_ms: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Max. parallele Aufrufe"
              fullWidth
              variant="standard"
              value={form.max_concurrency}
              helperText="Leer: lokal je Backend, extern seriell"
              onChange={(e) => update({ max_concurrency: e.target.value })}
            />
          </Box>
          <TextField
            margin="dense"
            label="Zusätzliche Body-Felder (JSON)"
            fullWidth
            variant="standard"
            value={form.extra_body}
            onChange={(e) => update({ extra_body: e.target.value })}
          />

          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <TextField
              label="Modell für den Chat-Test (optional)"
              variant="standard"
              fullWidth
              value={testModel}
              helperText="Name beim Provider. Ohne Angabe nur Erreichbarkeit und Key."
              onChange={(e) => setTestModel(e.target.value)}
            />
            <Button variant="outlined" color="secondary" disabled={testing} onClick={handleTest}>
              {testing ? <CircularProgress size={20} /> : "Testen"}
            </Button>
          </Box>
          {testResult && (
            <Alert severity={testResult.ok ? "success" : "error"} sx={{ mt: 1 }}>
              {testResult.steps.map((step) => (
                <div key={step.step}>
                  {step.step === "models" ? "Erreichbarkeit und Key" : `Chat mit ${step.model}`}
                  : {step.ok ? "OK" : "Fehler"} ({step.latency_ms} ms), {step.message}
                </div>
              ))}
              {!testResult.saved && <div>Geprüft wurde die Eingabe im Dialog. Nichts davon ist gespeichert.</div>}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeDialog}>Cancel</Button>
          <Button color="inherit" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAiProviders;
